<template>
    <section>
        <div class="max-w-screen-xl px-4 mx-auto grid lg:grid-cols-2 pt-10 mb-20 gap-12">
            <!-- Left Section -->
            <div class="relative">
                <!-- Icon -->
                <div class="absolute -left-10 -top-10 hidden sm:block">
                    <NuxtImg src="/images/circuit.png" width="200" height="120" alt="Shopping Bag" loading="lazy" />
                </div>
                <div
                    class="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl mb-6 bg-[image:var(--primary-gradient)]">
                    <NuxtImg src="/images/secure-icon.png" width="28" height="32" alt="Shopping Bag" loading="lazy" />
                </div>

                <!-- Heading -->
                <h2 class="text-2xl md:text-3xl text-[var(--text-secondary)] font-bold mb-2">
                    Try It for Yourself
                </h2>
                <p class="text-[var(--text-gray)] mb-8 text-lg md:text-xl">
                    Experience our premium frontend firsthand with a tailored demo or risk-free trial.
                    Discover how responsive design, dynamic content, and seamless integration elevate
                    your e-commerce platform.
                </p>

                <!-- Highlights -->
                <div>
                    <h3 class="text-xl md:text-2xl font-medium mb-4 text-[var(--text-secondary)]">
                        Additional Highlights:
                    </h3>
                    <div class="grid sm:grid-cols-2 gap-6">
                        <Card unstyled v-for="(item, index) in highlights" :key="index"
                            class="bg-[var(--background-card)] p-4 rounded-lg shadow-md">
                            <template #content>
                                <div class="flex items-start gap-3">
                                    <!-- Circle with light background -->
                                    <div
                                        class="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full p-3 bg-[var(--background-btn)]">
                                        <i
                                            :class="['pi', item.icon, 'text-[var(--text-secondary)]', 'text-base sm:text-lg']"></i>
                                    </div>
                                    <div>
                                        <h3 class="font-bold text-lg sm:text-xl text-[var(--text-primary)]">
                                            {{ item.title }}
                                        </h3>
                                        <p class="text-[var(--text-gray)] text-sm sm:text-base">
                                            {{ item.description }}
                                        </p>
                                    </div>
                                </div>
                            </template>
                        </Card>
                    </div>
                </div>
            </div>

            <!-- Right Section: Request Demo Form -->
            <Card unstyled class="bg-[var(--background-card)] p-6 rounded-lg">
                <template #content>
                    <h3 class="text-2xl md:text-3xl text-[var(--text-secondary)] font-medium mb-5">
                        Request Demo
                    </h3>
                    <form class="space-y-6 md:space-y-10">
                        <div class="grid sm:grid-cols-2 gap-6">
                            <div class="flex flex-col gap-1">
                                <label for="name" class="font-medium text-sm sm:text-base text-[var(--text-primary)]">
                                    Your name*
                                </label>
                                <InputText id="name" class="w-full !bg-[var(--background-input)]" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label for="email" class="font-medium text-sm sm:text-base text-[var(--text-primary)]">
                                    Email address*
                                </label>
                                <InputText id="email" class="w-full !bg-[var(--background-input)]" />
                            </div>
                        </div>
                        <div class="grid sm:grid-cols-2 gap-6">
                            <div class="flex flex-col gap-1">
                                <label for="phone" class="font-medium text-sm sm:text-base text-[var(--text-primary)]">
                                    Phone number
                                </label>
                                <InputText id="phone" class="w-full !bg-[var(--background-input)]" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label for="company"
                                    class="font-medium text-sm sm:text-base text-[var(--text-primary)]">
                                    Company name
                                </label>
                                <InputText id="company" class="w-full !bg-[var(--background-input)]" />
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <label for="message" class="font-medium text-sm sm:text-base text-[var(--text-primary)]">
                                Project description
                            </label>
                            <textarea id="message" rows="4"
                                class="w-full p-3 rounded-lg bg-[var(--background-input)] text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:outline-none"></textarea>
                        </div>

                        <div class="flex items-start sm:items-center gap-2">
                            <Checkbox v-model="accepted" binary inputId="privacy" />
                            <label for="privacy" class="text-sm sm:text-base text-[var(--text-gray)]">
                                I understand and agree to the
                                <a href="#" class="underline">Privacy Policy</a>
                            </label>
                        </div>

                        <Button unstyled iconPos="left" class="group !rounded-xl !px-4 sm:!px-5 
                            bg-[image:var(--primary-gradient)] hover:bg-[image:var(--primary-gradient-reverse)]
                            transition-all duration-1000 ease-in-out
                            !py-2.5 sm:!py-3 font-semibold
                            text-[var(--text-secondary)] 
                            flex items-center justify-center gap-2 sm:gap-3 
                            w-full sm:w-auto text-sm sm:text-base overflow-hidden">

                                <span class="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 
                                rounded-full bg-white 
                                transform transition-all duration-1000 ease-in-out 
                                group-hover:translate-x-28">
                                <i class="pi pi-angle-right text-green-500 font-bold text-sm sm:text-lg"></i>
                                </span>
                                <span class="transition-all duration-1000 ease-in-out group-hover:-translate-x-10">
                                Start free trial
                                </span>
                    </Button>
                    </form>
                </template>
            </Card>
        </div>
    </section>
</template>

<script setup lang="ts">

const accepted = ref(false);

const highlights = [
    { icon: "pi-video", title: "Live Demo", description: "Discover key features with a walkthrough of the interface." },
    { icon: "pi-gift", title: "Free Trial", description: "Test the full suite of features for a limited time." },
    { icon: "pi-ban", title: "No Commitment", description: "Evaluate the platform without any obligations." },
    { icon: "pi-compass", title: "Guided Setup", description: "Get step-by-step assistance during your demo or trial." },
];
</script>
